import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@services/api/client";
import { Link } from "react-router-dom";
import "@styles/FoodCourtsHome.css";

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

interface FoodCourt {
  foodCourtId: string;
  name: string;
  email: string;
  contactNumber: string;
  address: string;
  status: string;
  rating: number;
  reviewCount: number;
  isActive?: boolean;
  active?: boolean;
  verified: boolean;
  city: string;
  typeOfFood: string;
}

type StatusFilter = "ALL" | "APPROVED" | "PENDING" | "SUSPENDED" | "REJECTED";

const HomePage: React.FC = () => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("ALL");

  const { data, isLoading } = useQuery<FoodCourt[]>({
    queryKey: ["allFoodCourts"],
    queryFn: async () => {
      const res = await apiClient.get("/foodcourt/foodcourts?size=1000", authHeaders());
      return Array.isArray(res.data) ? res.data : res.data.content || [];
    },
    refetchOnWindowFocus: false,
  });

  const fcs = Array.isArray(data) ? data : [];
  const isActiveFC = (fc: FoodCourt) => fc.active ?? fc.isActive ?? false;

  const filtered = fcs
    .filter(fc => filter === "ALL" || fc.status === filter)
    .filter(fc => !search || fc.name?.toLowerCase().includes(search.toLowerCase()) || fc.city?.toLowerCase().includes(search.toLowerCase()));

  const counts = {
    ALL: fcs.length,
    APPROVED: fcs.filter(f => f.status === "APPROVED").length,
    PENDING: fcs.filter(f => f.status === "PENDING").length,
    SUSPENDED: fcs.filter(f => f.status === "SUSPENDED").length,
    REJECTED: fcs.filter(f => f.status === "REJECTED").length,
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "APPROVED": return "#10B981";
      case "PENDING": return "#F59E0B";
      case "SUSPENDED": return "#EF4444";
      case "REJECTED": return "#6B7280";
      default: return "#9CA3AF";
    }
  };

  return (
    <div className="fch">
      <div className="fch-header">
        <div>
          <h1 className="fch-title">All Food Courts</h1>
          <p className="fch-subtitle">Manage and monitor all registered food courts</p>
        </div>
        <div className="fch-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="text" placeholder="Search by name or city..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Stats Row */}
      <div className="fch-stats">
        {(["ALL", "APPROVED", "PENDING", "SUSPENDED", "REJECTED"] as StatusFilter[]).map(s => (
          <button key={s} className={`fch-stat-btn ${filter === s ? "active" : ""} ${s.toLowerCase()}`} onClick={() => setFilter(s)}>
            <span className="fch-stat-count">{counts[s]}</span>
            <span className="fch-stat-label">{s === "ALL" ? "Total" : s.charAt(0) + s.slice(1).toLowerCase()}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="fch-loading">
          <div className="fch-spinner"></div>
          <span>Loading food courts...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="fch-empty">No food courts found{search ? ` matching "${search}"` : ""}.</div>
      ) : (
        <div className="fch-table-wrap">
          <table className="fch-table">
            <thead>
              <tr>
                <th>Food Court</th>
                <th>City</th>
                <th>Contact</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Live</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(fc => (
                <tr key={fc.foodCourtId}>
                  <td>
                    <div className="fch-fc-info">
                      <span className="fch-fc-name">{fc.name}</span>
                      {fc.typeOfFood && <span className="fch-fc-type">{fc.typeOfFood}</span>}
                    </div>
                  </td>
                  <td className="fch-city">{fc.city || "—"}</td>
                  <td>
                    <div className="fch-contact">
                      {fc.contactNumber && <span>📞 {fc.contactNumber}</span>}
                      {fc.email && <span className="fch-email">{fc.email}</span>}
                    </div>
                  </td>
                  <td>
                    <span className="fch-rating">★ {fc.rating || 0}</span>
                    <span className="fch-reviews">({fc.reviewCount || 0})</span>
                  </td>
                  <td>
                    <span className="fch-status-badge" style={{ color: statusColor(fc.status), borderColor: statusColor(fc.status) }}>
                      {fc.status}
                    </span>
                  </td>
                  <td>
                    <span className={`fch-live-dot ${isActiveFC(fc) ? "on" : "off"}`}></span>
                  </td>
                  <td>
                    <Link to="/singlehotel" state={{ name: fc.name, status: fc.status, foodCourtId: fc.foodCourtId }} className="fch-view-btn">
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HomePage;
